import type { ApiResponse, PagedResponse, UserDto } from "@/types/api";
import type { BlogPostDetail, BlogSummary } from "@/types/blog";
import type {
  FaqItem,
  GalleryImage,
  PageDetail,
  PageSummary,
  LocationDto,
  StaffMember,
  Testimonial,
} from "@/types/page";
import type { TenantSettingsDto } from "@/types/tenant";
import type { PlatformTenant, PlatformTenantRequest } from "@/types/tenant";
import type {
  WordPressSource,
  WordPressSourceRequest,
  WordPressSyncResult,
} from "@/types/wordpress";
import type { ContentBlock } from "@/types/page";
import type { MenuItemDto } from "@/types/menu";
import type {
  EventDto,
  EventRequest,
  EventSource,
  EventSourceRequest,
  EventSyncResult,
} from "@/types/events";
import type { TaxonomyTerm, TaxonomyTermRequest, TaxonomyType } from "@/types/taxonomy";
import type { ProductDetail, ProductFilters, ProductRequest, ProductSummary } from "@/types/product";
import type { ProductMatchRequest, ProductMatchResponse } from "@/types/productMatch";
import type { Cart, CartItemRequest, CheckoutSession, Order } from "@/types/cart";
import type { TherapistDetail, TherapistFilters, TherapistRequest, TherapistSummary } from "@/types/therapist";
import type { TherapistMatchRequest, TherapistMatchResponse } from "@/types/therapistMatch";
import type { MenuCategory, MenuCategoryRequest, MenuItem, MenuItemRequest } from "@/types/restaurant";
import type {
  RadioLineup,
  RadioSchedule,
  RadioScheduleRequest,
  RadioShow,
  RadioShowRequest,
} from "@/types/radio";
import type { MusicRelease, MusicReleaseRequest } from "@/types/music";
import type { QrCodeItem, QrCodeRequest, ServiceOffering, ServiceOfferingRequest } from "@/types/services";
import type {
  Appointment,
  BookableService,
  BookableServiceRequest,
  BookingAvailability,
  BookingAvailabilityRequest,
  PublicBookingRequest,
  Slot,
} from "@/types/booking";
import type { CalendarConnection } from "@/types/calendar";
import type {
  ClubDocument,
  ClubDocumentRequest,
  ClubDues,
  ClubDuesCheckout,
  ClubDuesRequest,
  ClubMeeting,
  ClubMeetingRequest,
  ClubMember,
  ClubMemberRequest,
  ClubPublic,
  ClubRsvp,
  ClubRsvpCounts,
  ClubRsvpStatus,
  ClubTier,
  ClubTierRequest,
} from "@/types/club";
import type {
  GalleryAlbum as GalleryAlbumDto,
  GalleryAlbumRequest,
  GalleryImage as GalleryImageDto,
  GalleryImageRequest,
} from "@/types/gallery";

/**
 * Local-dev fallback only: production tenants are resolved per request from
 * the public hostname.
 */
const DEV_TENANT_SLUG = process.env.NEXT_PUBLIC_TENANT_SLUG;

const SERVER_BACKEND =
  process.env.BACKEND_URL || "http://localhost:8081";

/** Browser: same-origin `/api` proxy. Build-time server calls: direct backend URL. */
export function resolveApiBase(): string {
  if (typeof window !== "undefined") {
    return "/api";
  }
  return `${SERVER_BACKEND.replace(/\/$/, "")}/api`;
}

export const API_BASE = resolveApiBase();

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public errors?: Record<string, string[]> | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Prefer field-level API validation messages over the generic envelope message. */
export function formatApiError(error: unknown, fallback = "Request failed"): string {
  if (!(error instanceof ApiError)) {
    return fallback;
  }
  if (error.errors) {
    const details = Object.entries(error.errors)
      .flatMap(([field, messages]) => messages.map((message) => `${field}: ${message}`))
      .join("; ");
    if (details) {
      return details;
    }
  }
  return error.message || fallback;
}

type FetchOptions = RequestInit & {
  params?: Record<string, string | number | boolean | undefined>;
};

export function buildUrl(
  path: string,
  params?: FetchOptions["params"],
  baseOverride?: string,
): string {
  const base = (baseOverride || resolveApiBase()).replace(/\/$/, "");
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : process.env.NEXT_PUBLIC_SITE_URL || "http://localhost:3000";
  const url = base.startsWith("http")
    ? new URL(`${base}${normalized}`)
    : new URL(`${base}${normalized}`, origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
}

/**
 * During SSR, use the internal backend URL and forward the original public
 * host separately. Arbitrary tenant domains are not resolvable from inside
 * the frontend container, while the backend can still resolve the tenant from
 * X-Forwarded-Host.
 */
async function serverRequestApiBase(): Promise<string | undefined> {
  if (typeof window !== "undefined") {
    return undefined;
  }

  // SSR must fetch the current backend directly. Calling the public hostname
  // from inside Next can round-trip through Cloudflare/Traefik and return stale
  // tenant content; tenantHeaders() forwards the original public host separately.
  return `${SERVER_BACKEND.replace(/\/$/, "")}/api`;
}


/**
 * Per-request tenant identification. In the browser the Host header does the
 * job on its own. During SSR this is forwarded for routes that still use the
 * backend directly outside a request scope. Build/prerender falls back to the
 * dev slug when configured.
 */
async function tenantHeaders(): Promise<Record<string, string>> {
  if (typeof window === "undefined") {
    if (DEV_TENANT_SLUG) {
      return { "X-Tenant-Slug": DEV_TENANT_SLUG };
    }
    try {
      const { headers } = await import("next/headers");
      const requestHeaders = await headers();
      const forwardedHost = requestHeaders.get("x-wpcms-public-host")
        || requestHeaders.get("x-forwarded-host")?.split(",")[0]?.trim();
      const host = forwardedHost || requestHeaders.get("host");
      if (host) {
        const normalizedHost = host.split(":")[0]?.toLowerCase();
        if (normalizedHost === "purpleavenger.com" || normalizedHost === "www.purpleavenger.com") {
          return { "X-Tenant-Slug": "purple-avenger" };
        }
        return { "X-Forwarded-Host": host };
      }
    } catch {
      // Not in a request scope (build/prerender); fall through.
    }
  }
  return DEV_TENANT_SLUG ? { "X-Tenant-Slug": DEV_TENANT_SLUG } : {};
}

async function serverCookieHeader(): Promise<string | undefined> {
  if (typeof window !== "undefined") {
    return undefined;
  }
  const { cookies } = await import("next/headers");
  const jar = cookies();
  const parts: string[] = [];
  const auth = jar.get("auth_token");
  const pageUnlock = jar.get("page_unlock");
  const cartToken = jar.get("cart_token");
  if (auth) parts.push(`auth_token=${auth.value}`);
  if (pageUnlock) parts.push(`page_unlock=${pageUnlock.value}`);
  if (cartToken) parts.push(`cart_token=${cartToken.value}`);
  return parts.length > 0 ? parts.join("; ") : undefined;
}

export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {},
): Promise<T> {
  const { params, headers, ...init } = options;
  const requestApiBase = await serverRequestApiBase();
  const url = buildUrl(path, params, requestApiBase);
  const cookieHeader = await serverCookieHeader();
  const tenantHeader = await tenantHeaders();

  const response = await fetch(url, {
    ...init,
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...tenantHeader,
      ...(cookieHeader ? { Cookie: cookieHeader } : {}),
      ...(init.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...headers,
    },
  });

  const responseText = await response.text();
  let envelope: ApiResponse<T>;
  try {
    envelope = JSON.parse(responseText) as ApiResponse<T>;
  } catch {
    const detail = responseText.trim().slice(0, 200);
    throw new ApiError(detail || "Invalid API response", response.status);
  }

  if (!response.ok || !envelope.success) {
    throw new ApiError(
      envelope.message || response.statusText || "Request failed",
      response.status,
      envelope.errors,
    );
  }

  return envelope.data;
}

export async function fetchTenantSettings(): Promise<TenantSettingsDto> {
  return apiFetch<TenantSettingsDto>("/tenant/settings");
}

export async function fetchNavPages(): Promise<PageSummary[]> {
  return apiFetch<PageSummary[]>("/pages");
}

export async function fetchAdminPages(): Promise<PageSummary[]> {
  return apiFetch<PageSummary[]>("/admin/pages");
}

export async function fetchAdminPage(id: string): Promise<PageDetail> {
  return apiFetch<PageDetail>(`/admin/pages/${id}`);
}

export async function fetchPage(slug: string): Promise<PageDetail | null> {
  try {
    return await apiFetch<PageDetail>(`/pages/${slug}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function fetchFrontPage(): Promise<PageDetail | null> {
  try {
    return await apiFetch<PageDetail>("/pages/front");
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function fetchNotFoundPage(): Promise<PageDetail | null> {
  try {
    return await apiFetch<PageDetail>("/pages/not-found");
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function fetchMenu(location: string): Promise<MenuItemDto[]> {
  return apiFetch<MenuItemDto[]>(`/menus/${location}`);
}

export interface ReadingSettingsDto {
  frontPageId: string | null;
  postsPageId: string | null;
}

export async function fetchReadingSettings(): Promise<ReadingSettingsDto> {
  return apiFetch<ReadingSettingsDto>("/admin/pages/reading");
}

export async function updateReadingSettings(data: ReadingSettingsDto): Promise<ReadingSettingsDto> {
  return apiFetch<ReadingSettingsDto>("/admin/pages/reading", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export interface SearchHit {
  type: string;
  id: string;
  title: string;
  href: string;
  excerpt: string | null;
}

export interface SearchResults {
  pages: SearchHit[];
  posts: SearchHit[];
  products: SearchHit[];
  therapists: SearchHit[];
}

export async function searchSite(q: string): Promise<SearchResults> {
  return apiFetch<SearchResults>("/search", { params: { q } });
}

export async function fetchStaff(pageId: string): Promise<StaffMember[]> {
  return apiFetch<StaffMember[]>("/staff", { params: { pageId } });
}

export async function fetchAllStaff(): Promise<StaffMember[]> {
  return apiFetch<StaffMember[]>("/staff");
}

export interface CreateStaffRequest {
  pageId: string;
  name: string;
  title?: string;
  bio?: string;
  photoUrl?: string;
  email?: string;
  sortOrder?: number;
  published?: boolean;
  socialLinks?: Record<string, string>;
}

export interface UpdateStaffRequest {
  name?: string;
  title?: string;
  bio?: string;
  photoUrl?: string;
  email?: string;
  sortOrder?: number;
  published?: boolean;
  socialLinks?: Record<string, string>;
}

export async function fetchAdminStaff(pageId: string): Promise<StaffMember[]> {
  return apiFetch<StaffMember[]>("/admin/staff", { params: { pageId } });
}

export async function fetchAllAdminStaff(): Promise<StaffMember[]> {
  return apiFetch<StaffMember[]>("/admin/staff/all");
}

export async function createStaffMember(data: CreateStaffRequest): Promise<StaffMember> {
  return apiFetch<StaffMember>("/admin/staff", { method: "POST", body: JSON.stringify(data) });
}

export async function updateStaffMember(id: string, data: UpdateStaffRequest): Promise<StaffMember> {
  return apiFetch<StaffMember>(`/admin/staff/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteStaffMember(id: string): Promise<void> {
  await apiFetch<void>(`/admin/staff/${id}`, { method: "DELETE" });
}

export async function fetchFaq(pageId: string): Promise<FaqItem[]> {
  return apiFetch<FaqItem[]>("/faq", { params: { pageId } });
}

export async function fetchGallery(pageId: string): Promise<GalleryImage[]> {
  return apiFetch<GalleryImage[]>("/gallery", { params: { pageId } });
}

export type LocationRequest = Omit<LocationDto, "id">;

export async function fetchLocations(): Promise<LocationDto[]> {
  return apiFetch<LocationDto[]>("/locations");
}

export async function fetchLocation(slug: string): Promise<LocationDto | null> {
  try {
    return await apiFetch<LocationDto>(`/locations/${slug}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function fetchAdminLocations(): Promise<LocationDto[]> {
  return apiFetch<LocationDto[]>("/admin/locations");
}

export async function createLocation(data: LocationRequest): Promise<LocationDto> {
  return apiFetch<LocationDto>("/admin/locations", { method: "POST", body: JSON.stringify(data) });
}

export async function updateLocation(id: string, data: LocationRequest): Promise<LocationDto> {
  return apiFetch<LocationDto>(`/admin/locations/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteLocation(id: string): Promise<void> {
  await apiFetch<void>(`/admin/locations/${id}`, { method: "DELETE" });
}

export interface TestimonialRequest {
  author: string;
  role?: string | null;
  avatarUrl?: string | null;
  rating?: number | null;
  body: string;
  sourceLink?: string | null;
  moderationStatus?: "pending" | "approved" | "rejected";
  featured?: boolean;
  sortOrder?: number;
}

export async function fetchTestimonials(limit = 6): Promise<Testimonial[]> {
  return apiFetch<Testimonial[]>("/testimonials", { params: { limit } });
}

export async function submitTestimonial(data: {
  author: string;
  role?: string;
  rating?: number;
  body: string;
  sourceLink?: string;
  submittedEmail?: string;
  captchaToken?: string;
  honeypotField?: string;
  honeypot?: Record<string, unknown>;
}): Promise<Testimonial> {
  return apiFetch<Testimonial>("/testimonials", { method: "POST", body: JSON.stringify(data) });
}

export async function fetchAdminTestimonials(): Promise<Testimonial[]> {
  return apiFetch<Testimonial[]>("/admin/testimonials");
}

export async function createTestimonial(data: TestimonialRequest): Promise<Testimonial> {
  return apiFetch<Testimonial>("/admin/testimonials", { method: "POST", body: JSON.stringify(data) });
}

export async function updateTestimonial(id: string, data: TestimonialRequest): Promise<Testimonial> {
  return apiFetch<Testimonial>(`/admin/testimonials/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function moderateTestimonial(
  id: string,
  moderationStatus: "pending" | "approved" | "rejected",
): Promise<Testimonial> {
  return apiFetch<Testimonial>(`/admin/testimonials/${id}/moderation`, {
    method: "PATCH",
    body: JSON.stringify({ moderationStatus }),
  });
}

export async function deleteTestimonial(id: string): Promise<void> {
  await apiFetch<void>(`/admin/testimonials/${id}`, { method: "DELETE" });
}

export async function fetchBlogPosts(
  page = 0,
  size = 12,
): Promise<PagedResponse<BlogSummary>> {
  return apiFetch<PagedResponse<BlogSummary>>("/blog", {
    params: { page, size },
  });
}

export async function fetchBlogPost(slug: string): Promise<BlogPostDetail | null> {
  try {
    return await apiFetch<BlogPostDetail>(`/blog/${slug}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export async function incrementBlogView(slug: string): Promise<number> {
  return apiFetch<number>(`/blog/${slug}/view`, { method: "POST" });
}

export async function likeBlogPost(slug: string): Promise<number> {
  return apiFetch<number>(`/blog/${slug}/like`, { method: "POST" });
}

export async function unlikeBlogPost(slug: string): Promise<number> {
  return apiFetch<number>(`/blog/${slug}/like`, { method: "DELETE" });
}

export async function submitContact(data: {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
}) {
  return apiFetch<unknown>("/contact", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function fetchCurrentUser(): Promise<UserDto | null> {
  try {
    return await apiFetch<UserDto>("/auth/me");
  } catch {
    return null;
  }
}

export interface AdminUserDto {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  active: boolean;
  emailVerified: boolean;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminUserRequest {
  email: string;
  displayName?: string | null;
  role: string;
  active: boolean;
}

export interface AuthSessionDto {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  revokedAt: string | null;
  active: boolean;
}

export interface TotpSetupDto {
  secret: string;
  otpauthUrl: string;
}

export async function fetchAdminUsers(): Promise<AdminUserDto[]> {
  return apiFetch<AdminUserDto[]>("/admin/users");
}

export async function createAdminUser(data: AdminUserRequest): Promise<AdminUserDto> {
  return apiFetch<AdminUserDto>("/admin/users", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminUser(id: string, data: AdminUserRequest): Promise<AdminUserDto> {
  return apiFetch<AdminUserDto>(`/admin/users/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function inviteAdminUser(id: string): Promise<void> {
  await apiFetch<void>(`/admin/users/${id}/invite`, { method: "POST" });
}

export async function deleteAdminUser(id: string): Promise<void> {
  await apiFetch<void>(`/admin/users/${id}`, { method: "DELETE" });
}

export async function startTotpSetup(): Promise<TotpSetupDto> {
  return apiFetch<TotpSetupDto>("/auth/totp/setup", { method: "POST" });
}

export async function confirmTotpSetup(code: string): Promise<UserDto> {
  return apiFetch<UserDto>("/auth/totp/confirm", { method: "POST", body: JSON.stringify({ code }) });
}

export async function fetchAuthSessions(): Promise<AuthSessionDto[]> {
  return apiFetch<AuthSessionDto[]>("/auth/sessions");
}

export async function revokeAuthSession(id: string): Promise<void> {
  await apiFetch<void>(`/auth/sessions/${id}`, { method: "DELETE" });
}

export interface MediaFileDto {
  id: string;
  filename: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  altText: string | null;
  decorative: boolean;
}

export interface ContactSubmissionDto {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string | null;
  message: string;
  readAt: string | null;
  createdAt: string;
}

export async function fetchAdminContacts(): Promise<ContactSubmissionDto[]> {
  return apiFetch<ContactSubmissionDto[]>("/admin/contacts");
}

export async function markContactRead(id: string): Promise<ContactSubmissionDto> {
  return apiFetch<ContactSubmissionDto>(`/admin/contacts/${id}/read`, {
    method: "PATCH",
  });
}

export async function fetchAdminMedia(): Promise<MediaFileDto[]> {
  return apiFetch<MediaFileDto[]>("/admin/media");
}

export async function uploadMedia(
  file: File,
  altText?: string,
  decorative = false,
  onProgress?: (pct: number) => void,
): Promise<MediaFileDto> {
  const form = new FormData();
  form.append("file", file);
  if (altText) form.append("altText", altText);
  form.append("decorative", String(decorative));

  const xhr = new Promise<MediaFileDto>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", buildUrl("/admin/media"));
    request.withCredentials = true;
    request.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    if (DEV_TENANT_SLUG) {
      request.setRequestHeader("X-Tenant-Slug", DEV_TENANT_SLUG);
    }

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onload = () => {
      try {
        const envelope = JSON.parse(request.responseText) as ApiResponse<MediaFileDto>;
        if (request.status >= 200 && request.status < 300 && envelope.success) {
          resolve(envelope.data);
        } else {
          reject(
            new ApiError(
              envelope.message || "Upload failed",
              request.status,
              envelope.errors,
            ),
          );
        }
      } catch {
        reject(new ApiError("Upload failed", request.status));
      }
    };

    request.onerror = () => reject(new ApiError("Upload failed", 0));
    request.send(form);
  });

  return xhr;
}

export async function deleteMedia(id: string): Promise<void> {
  await apiFetch<void>(`/admin/media/${id}`, { method: "DELETE" });
}

export async function createPage(data: {
  slug: string;
  title: string;
  pageType: string;
  layout?: string;
  navOrder?: number;
  metaTitle?: string;
  metaDescription?: string;
  ogImageUrl?: string;
  config?: Record<string, unknown>;
}): Promise<PageDetail> {
  return apiFetch<PageDetail>("/admin/pages", {
    method: "POST",
    body: JSON.stringify({ ...data, pageType: data.pageType.toUpperCase() }),
  });
}

export async function updatePage(
  id: string,
  data: Record<string, unknown>,
): Promise<PageDetail> {
  return apiFetch<PageDetail>(`/admin/pages/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function savePageContent(id: string, page: Record<string, unknown>, blocks: ContentBlock[], publish = false): Promise<PageDetail> {
  return apiFetch<PageDetail>(`/admin/pages/${id}/content`, {
    method: "PUT",
    body: JSON.stringify({ page, publish, blocks: blocks.map(block => ({
      id: block.id.startsWith("draft-") ? null : block.id,
      blockType: block.blockType, content: block.content, published: block.published,
    })) }),
  });
}

export interface PageDraft { version: number; hasDraft: boolean; page: PageDetail }
export async function fetchPageDraft(id: string): Promise<PageDraft> {
  const result = await apiFetch<PageDraft>(`/admin/pages/${id}/draft`);
  result.page.blocks = result.page.blocks.map((block, index) => ({ ...block, id: block.id ?? `draft-${index}` }));
  return result;
}
export async function savePageDraft(id: string, version: number, page: Record<string, unknown>, blocks: ContentBlock[]): Promise<PageDraft> {
  return apiFetch<PageDraft>(`/admin/pages/${id}/draft`, { method: "PUT", body: JSON.stringify({ version,
    content: { page, publish: false, blocks: blocks.map(block => ({
      id: block.id.startsWith("draft-") ? null : block.id,
      blockType: block.blockType, content: block.content, published: block.published,
    })) },
  }) });
}
export async function publishPageDraft(id: string, version: number, pagePassword?: string): Promise<PageDraft> {
  return apiFetch<PageDraft>(`/admin/pages/${id}/draft/publish`, { method: "POST", body: JSON.stringify({ version, pagePassword }) });
}

export async function publishPage(
  id: string,
  published: boolean,
): Promise<PageDetail> {
  return apiFetch<PageDetail>(`/admin/pages/${id}/publish`, {
    method: "PATCH",
    body: JSON.stringify({ published }),
  });
}

export async function deletePage(id: string): Promise<void> {
  await apiFetch<void>(`/admin/pages/${id}`, { method: "DELETE" });
}

export async function reorderBlocks(
  pageId: string,
  orderedIds: string[],
): Promise<PageDetail["blocks"]> {
  return apiFetch<PageDetail["blocks"]>(`/admin/pages/${pageId}/blocks/order`, {
    method: "PATCH",
    body: JSON.stringify({ orderedIds }),
  });
}

export async function createContentBlock(
  pageId: string,
  data: {
    blockType: string;
    content?: Record<string, unknown>;
    sortOrder?: number;
    published?: boolean;
  },
): Promise<ContentBlock> {
  return apiFetch<ContentBlock>(`/admin/pages/${pageId}/blocks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateContentBlock(
  pageId: string,
  blockId: string,
  data: {
    blockType?: string;
    content?: Record<string, unknown>;
    sortOrder?: number;
    published?: boolean;
  },
): Promise<ContentBlock> {
  return apiFetch<ContentBlock>(`/admin/pages/${pageId}/blocks/${blockId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteContentBlock(
  pageId: string,
  blockId: string,
): Promise<void> {
  await apiFetch<void>(`/admin/pages/${pageId}/blocks/${blockId}`, {
    method: "DELETE",
  });
}

export async function fetchAdminSettings(): Promise<TenantSettingsDto> {
  return apiFetch<TenantSettingsDto>("/admin/settings");
}

export async function updateAdminSettings(data: {
  name: string;
  settings: Record<string, unknown>;
  contrastOverride?: boolean;
}): Promise<TenantSettingsDto> {
  return apiFetch<TenantSettingsDto>("/admin/settings", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function generateFavicon(): Promise<MediaFileDto> {
  return apiFetch<MediaFileDto>("/admin/favicon/generate", { method: "GET" });
}

export async function createBlogPost(data: {
  pageId: string;
  slug: string;
  title: string;
  excerpt?: string;
  body: string;
  featuredImage?: string;
  tags?: string[];
  categoryIds?: string[];
  sticky?: boolean;
  metaTitle?: string;
  metaDescription?: string;
}): Promise<BlogPostDetail> {
  return apiFetch<BlogPostDetail>("/admin/blog", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateBlogPost(
  id: string,
  data: Record<string, unknown>,
): Promise<BlogPostDetail> {
  return apiFetch<BlogPostDetail>(`/admin/blog/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function publishBlogPost(
  id: string,
  published: boolean,
): Promise<BlogPostDetail> {
  return apiFetch<BlogPostDetail>(`/admin/blog/${id}/publish`, {
    method: "PATCH",
    body: JSON.stringify({ published }),
  });
}

export async function deleteBlogPost(id: string): Promise<void> {
  await apiFetch<void>(`/admin/blog/${id}`, { method: "DELETE" });
}

export async function unlockPage(slug: string, password: string): Promise<void> {
  await apiFetch<void>(`/pages/${slug}/unlock`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export interface CategoryTree {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  description: string | null;
  children: CategoryTree[];
}

export async function fetchCategories(): Promise<CategoryTree[]> {
  return apiFetch<CategoryTree[]>("/categories");
}

export async function createCategory(data: {
  name: string;
  slug: string;
  description?: string;
  parentId?: string | null;
}) {
  return apiFetch<{ id: string; name: string; slug: string }>("/admin/categories", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateCategory(
  id: string,
  data: { name?: string; slug?: string; description?: string; parentId?: string | null },
) {
  return apiFetch<{ id: string; name: string; slug: string }>(`/admin/categories/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(id: string): Promise<void> {
  await apiFetch<void>(`/admin/categories/${id}`, { method: "DELETE" });
}

export async function recordEvent(
  eventType: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  await apiFetch<unknown>("/events", {
    method: "POST",
    body: JSON.stringify({ eventType, metadata }),
  });
}

export interface AnalyticsDailyMetric {
  day: string;
  pageViews: number;
  uniqueVisitors: number;
}

export interface AnalyticsDimensionMetric {
  dimension: string;
  count: number;
}

export interface AnalyticsSummary {
  startDate: string;
  endDate: string;
  pageViews: number;
  uniqueVisitors: number;
  siteSearches: number;
  formConversions: number;
  engagementEvents: number;
  weekOverWeekPageViewChange: number;
  daily: AnalyticsDailyMetric[];
  topPages: AnalyticsDimensionMetric[];
  topReferrers: AnalyticsDimensionMetric[];
  deviceBreakdown: AnalyticsDimensionMetric[];
}

export type SiteHealthStatus = "OK" | "WARN" | "CRIT";

export type SiteHealthCheckType =
  | "DOMAIN_TLS"
  | "UPTIME"
  | "WP_SYNC"
  | "CONTENT_FRESHNESS"
  | "A11Y_SCORE"
  | "BROKEN_LINKS"
  | "MEDIA_STORAGE"
  | "BACKUP_AGE";

export interface SiteHealthCheck {
  checkType: SiteHealthCheckType;
  status: SiteHealthStatus;
  reason: string;
  details: Record<string, unknown>;
  checkedAt: string;
}

export interface SiteHealth {
  tenantId: string;
  overallStatus: SiteHealthStatus;
  checks: SiteHealthCheck[];
}

export interface CommandCenterTrafficPoint {
  day: string;
  pageViews: number;
}

export interface PlatformCommandCenterSite {
  tenantId: string;
  slug: string;
  name: string;
  domain: string | null;
  active: boolean;
  healthStatus: SiteHealthStatus;
  healthReason: string;
  unresolvedErrors: number;
  activeVisitors: number;
  sevenDayPageViews: number;
  traffic: CommandCenterTrafficPoint[];
  lastClientAdminLoginAt: string | null;
  lastContentEditAt: string | null;
  openSubmissions: number;
  mediaBytes: number;
  wordPressSyncStatus: string;
  planTier: string;
  screenshotUrl: string | null;
  routingStatus: string;
  dnsPointsHere: boolean;
  traefikRouted: boolean;
  certificateValid: boolean;
}

export interface PlatformResourceUsage {
  mediaBytes: number;
  mediaDiskTotalBytes: number;
  mediaDiskUsableBytes: number;
  databaseBytes: number;
  jvmUsedBytes: number;
  jvmMaxBytes: number;
  availableProcessors: number;
  systemLoadAverage: number;
  measuredAt: string;
}

export interface PlatformUsage {
  tenantId: string;
  slug: string;
  name: string;
  domain: string | null;
  active: boolean;
  planTier: string;
  mediaBytes: number;
  mediaFiles: number;
  pages: number;
  blogPosts: number;
  contentRows: number;
  pageViews30d: number;
}

export interface OperationalError {
  id: string;
  source: string;
  exceptionType: string;
  summary: string;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
}

export interface PreviewCaptureStatus {
  tenantId: string;
  status: "PENDING" | "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED";
  requestedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  errorMessage: string | null;
  screenshotUrl: string | null;
}

export interface AuditEntry {
  id: string;
  actorId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface AuditFilters extends Record<string, string | undefined> {
  entityType?: string;
  action?: string;
  actorId?: string;
  from?: string;
  to?: string;
  page?: string;
  size?: string;
}

export interface PlatformSubmission {
  id: string;
  source: "contact" | "form";
  sourceName: string;
  name: string;
  email: string | null;
  subject: string | null;
  open: boolean;
  unansweredHours: number;
  slaBreached: boolean;
  readAt: string | null;
  repliedAt: string | null;
  createdAt: string;
}

export interface PlatformDigest {
  tenantId: string;
  tenantName: string;
  startDate: string;
  endDate: string;
  pageViews: number;
  uniqueVisitors: number;
  weekOverWeekPageViewChange: number;
  formConversions: number;
  openSubmissions: number;
  healthStatus: SiteHealthStatus;
  healthReason: string;
  latestContentEdit: string | null;
}

export interface PlatformDigestSendResult {
  tenantId: string;
  tenantName: string;
  recipients: string[];
  digest: PlatformDigest;
}

export async function fetchAdminAnalyticsSummary(days = 30): Promise<AnalyticsSummary> {
  return apiFetch<AnalyticsSummary>("/admin/analytics/summary", { params: { days } });
}

export async function refreshAdminAnalyticsSummary(days = 30): Promise<AnalyticsSummary> {
  return apiFetch<AnalyticsSummary>("/admin/analytics/rollup", {
    method: "POST",
    params: { days },
  });
}

export async function fetchPlatformAnalyticsSummary(
  tenantId: string,
  days = 30,
): Promise<AnalyticsSummary> {
  return apiFetch<AnalyticsSummary>(`/platform/analytics/${tenantId}`, { params: { days } });
}

export async function fetchAdminSiteHealth(): Promise<SiteHealth> {
  return apiFetch<SiteHealth>("/admin/site-health");
}

export async function refreshAdminSiteHealth(): Promise<SiteHealth> {
  return apiFetch<SiteHealth>("/admin/site-health/refresh", { method: "POST" });
}

export async function fetchPlatformSiteHealth(tenantId: string): Promise<SiteHealth> {
  return apiFetch<SiteHealth>(`/platform/site-health/${tenantId}`);
}

export async function refreshPlatformSiteHealth(tenantId: string): Promise<SiteHealth> {
  return apiFetch<SiteHealth>(`/platform/site-health/${tenantId}/refresh`, { method: "POST" });
}

export async function fetchPlatformCommandCenterSites(): Promise<PlatformCommandCenterSite[]> {
  return apiFetch<PlatformCommandCenterSite[]>("/platform/command-center/sites");
}

export async function fetchPlatformResourceUsage(): Promise<PlatformResourceUsage> {
  return apiFetch<PlatformResourceUsage>("/platform/command-center/resources");
}

export async function fetchPlatformUsage(): Promise<PlatformUsage[]> {
  return apiFetch<PlatformUsage[]>("/platform/usage");
}

export async function fetchPlatformUsers(tenantId: string): Promise<AdminUserDto[]> {
  return apiFetch<AdminUserDto[]>(`/platform/tenants/${tenantId}/users`);
}

export async function createPlatformUser(tenantId: string, data: AdminUserRequest): Promise<AdminUserDto> {
  return apiFetch<AdminUserDto>(`/platform/tenants/${tenantId}/users`, { method: "POST", body: JSON.stringify(data) });
}

export async function updatePlatformUser(tenantId: string, userId: string, data: AdminUserRequest): Promise<AdminUserDto> {
  return apiFetch<AdminUserDto>(`/platform/tenants/${tenantId}/users/${userId}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function invitePlatformUser(tenantId: string, userId: string): Promise<void> {
  await apiFetch<void>(`/platform/tenants/${tenantId}/users/${userId}/invite`, { method: "POST" });
}

export async function deletePlatformUser(tenantId: string, userId: string): Promise<void> {
  await apiFetch<void>(`/platform/tenants/${tenantId}/users/${userId}`, { method: "DELETE" });
}

export async function fetchAdminAudit(filters: AuditFilters = {}): Promise<AuditEntry[]> {
  return apiFetch<AuditEntry[]>("/admin/audit", { params: { page: "0", size: "50", ...filters } });
}

export async function fetchPlatformAudit(
  tenantId: string,
  filters: AuditFilters = {},
): Promise<AuditEntry[]> {
  return apiFetch<AuditEntry[]>(`/platform/audit/${tenantId}`, { params: { page: "0", size: "50", ...filters } });
}

export async function fetchPlatformErrors(tenantId: string): Promise<OperationalError[]> {
  return apiFetch<OperationalError[]>(`/platform/errors/${tenantId}`, { params: { page: "0", size: "50" } });
}

export async function resolvePlatformError(tenantId: string, errorId: string): Promise<void> {
  await apiFetch<void>(`/platform/errors/${tenantId}/${errorId}/resolve`, { method: "POST" });
}

export async function fetchPlatformPreviewStatus(tenantId: string): Promise<PreviewCaptureStatus> {
  return apiFetch<PreviewCaptureStatus>(`/platform/previews/${tenantId}`);
}

export async function requestPlatformPreviewRefresh(tenantId: string): Promise<PreviewCaptureStatus> {
  return apiFetch<PreviewCaptureStatus>(`/platform/previews/${tenantId}/refresh`, { method: "POST" });
}

export async function fetchAdminErrors(): Promise<OperationalError[]> {
  return apiFetch<OperationalError[]>("/admin/errors", { params: { page: "0", size: "50" } });
}

export async function fetchPlatformSubmissions(tenantId: string): Promise<PlatformSubmission[]> {
  return apiFetch<PlatformSubmission[]>(`/platform/submissions/${tenantId}`);
}

export async function sendPlatformWeeklyDigest(tenantId: string): Promise<PlatformDigestSendResult> {
  return apiFetch<PlatformDigestSendResult>(`/platform/digests/${tenantId}/weekly/send`, { method: "POST" });
}

export interface AdminMenuItem {
  id: string;
  parentId: string | null;
  label: string;
  url: string | null;
  pageId: string | null;
  target: string;
  sortOrder: number;
}

export interface AdminMenu {
  id: string | null;
  location: string;
  name: string;
  items: AdminMenuItem[];
}

export async function fetchAdminMenu(location: string): Promise<AdminMenu> {
  return apiFetch<AdminMenu>(`/admin/menus/${location}`);
}

export async function saveAdminMenu(
  location: string,
  data: {
    name?: string;
    items: Array<{
      parentId?: string | null;
      label: string;
      url?: string | null;
      pageId?: string | null;
      target?: string;
      sortOrder?: number;
    }>;
  },
): Promise<AdminMenu> {
  return apiFetch<AdminMenu>(`/admin/menus/${location}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export interface RevisionSummary {
  id: string;
  authorId: string | null;
  createdAt: string;
}

export async function fetchRevisions(
  entityType: "page" | "blog_post",
  entityId: string,
): Promise<RevisionSummary[]> {
  return apiFetch<RevisionSummary[]>("/admin/revisions", {
    params: { entityType, entityId },
  });
}

export async function restoreRevision(id: string): Promise<void> {
  await apiFetch<void>(`/admin/revisions/${id}/restore`, { method: "POST" });
}

export interface BlockPattern {
  id: string;
  name: string;
  category: string;
  thumbnailUrl: string | null;
  blocks: Array<{ blockType: string; content: Record<string, unknown> }>;
  system: boolean;
}

export async function fetchBlockPatterns(): Promise<BlockPattern[]> {
  return apiFetch<BlockPattern[]>("/admin/block-patterns");
}

export async function createBlockPattern(data: {
  name: string;
  category: string;
  blocks: Array<{ blockType: string; content: Record<string, unknown> }>;
}): Promise<BlockPattern> {
  return apiFetch<BlockPattern>("/admin/block-patterns", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteBlockPattern(id: string): Promise<void> {
  await apiFetch<void>(`/admin/block-patterns/${id}`, { method: "DELETE" });
}

export async function fetchAdminBlogPosts(): Promise<
  Array<BlogSummary & { published: boolean }>
> {
  return apiFetch("/admin/blog");
}

export async function fetchAdminBlogPost(id: string): Promise<BlogPostDetail> {
  return apiFetch<BlogPostDetail>(`/admin/blog/${id}`);
}

export async function fetchBlogByCategory(
  slug: string,
  page = 0,
  size = 12,
): Promise<PagedResponse<BlogSummary>> {
  return apiFetch<PagedResponse<BlogSummary>>(`/blog/category/${slug}`, {
    params: { page, size },
  });
}

export function mediaUrl(storagePath: string): string {
  const base =
    typeof window !== "undefined"
      ? ""
      : SERVER_BACKEND.replace(/\/$/, "");
  return `${base}/media/${storagePath}`;
}

export async function fetchWordPressSource(): Promise<WordPressSource | null> {
  return apiFetch<WordPressSource | null>("/admin/wordpress-source");
}

export async function saveWordPressSource(
  data: WordPressSourceRequest,
): Promise<WordPressSource> {
  return apiFetch<WordPressSource>("/admin/wordpress-source", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteWordPressSource(): Promise<void> {
  await apiFetch<void>("/admin/wordpress-source", { method: "DELETE" });
}

export async function rotateWordPressSecret(): Promise<WordPressSource> {
  return apiFetch<WordPressSource>("/admin/wordpress-source/rotate-secret", { method: "POST" });
}

export async function syncWordPressNow(): Promise<WordPressSyncResult> {
  return apiFetch<WordPressSyncResult>("/admin/wordpress-source/sync", { method: "POST" });
}

export async function fetchPlatformWordPressSource(tenantId: string): Promise<WordPressSource | null> {
  return apiFetch<WordPressSource | null>(`/platform/wordpress-source/${tenantId}`);
}

export async function syncPlatformWordPressNow(tenantId: string): Promise<WordPressSyncResult> {
  return apiFetch<WordPressSyncResult>(`/platform/wordpress-source/${tenantId}/sync`, { method: "POST" });
}

export async function fetchEvents(params?: {
  from?: string;
  through?: string;
  category?: string;
}): Promise<EventDto[]> {
  return apiFetch<EventDto[]>("/site-events", { params });
}

export async function fetchEvent(slug: string): Promise<EventDto> {
  return apiFetch<EventDto>(`/site-events/${encodeURIComponent(slug)}`);
}

export async function fetchAdminEvents(): Promise<EventDto[]> {
  return apiFetch<EventDto[]>("/admin/events");
}

export async function createAdminEvent(data: EventRequest): Promise<EventDto> {
  return apiFetch<EventDto>("/admin/events", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminEvent(id: string, data: EventRequest): Promise<EventDto> {
  return apiFetch<EventDto>(`/admin/events/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminEvent(id: string): Promise<void> {
  await apiFetch<void>(`/admin/events/${id}`, { method: "DELETE" });
}

export async function syncAdminEvents(): Promise<EventSyncResult> {
  return apiFetch<EventSyncResult>("/admin/events/sync", { method: "POST" });
}

export async function fetchEventSource(): Promise<EventSource | null> {
  return apiFetch<EventSource | null>("/admin/event-source");
}

export async function saveEventSource(data: EventSourceRequest): Promise<EventSource> {
  return apiFetch<EventSource>("/admin/event-source", { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteEventSource(): Promise<void> {
  await apiFetch<void>("/admin/event-source", { method: "DELETE" });
}

export async function fetchMusicReleases(): Promise<MusicRelease[]> {
  return apiFetch<MusicRelease[]>("/music");
}

export async function fetchMusicRelease(slug: string): Promise<MusicRelease> {
  return apiFetch<MusicRelease>(`/music/${encodeURIComponent(slug)}`);
}

export async function fetchAdminMusicReleases(): Promise<MusicRelease[]> {
  return apiFetch<MusicRelease[]>("/admin/music");
}

export async function createAdminMusicRelease(data: MusicReleaseRequest): Promise<MusicRelease> {
  return apiFetch<MusicRelease>("/admin/music", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminMusicRelease(
  id: string,
  data: MusicReleaseRequest,
): Promise<MusicRelease> {
  return apiFetch<MusicRelease>(`/admin/music/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminMusicRelease(id: string): Promise<void> {
  await apiFetch<void>(`/admin/music/${id}`, { method: "DELETE" });
}

export async function fetchServiceOfferings(): Promise<ServiceOffering[]> {
  return apiFetch<ServiceOffering[]>("/services");
}

export async function fetchServiceOffering(slug: string): Promise<ServiceOffering> {
  return apiFetch<ServiceOffering>(`/services/${encodeURIComponent(slug)}`);
}

export async function fetchAdminServiceOfferings(): Promise<ServiceOffering[]> {
  return apiFetch<ServiceOffering[]>("/admin/services");
}

export async function createAdminServiceOffering(data: ServiceOfferingRequest): Promise<ServiceOffering> {
  return apiFetch<ServiceOffering>("/admin/services", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminServiceOffering(
  id: string,
  data: ServiceOfferingRequest,
): Promise<ServiceOffering> {
  return apiFetch<ServiceOffering>(`/admin/services/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminServiceOffering(id: string): Promise<void> {
  await apiFetch<void>(`/admin/services/${id}`, { method: "DELETE" });
}

export async function fetchAdminQrCodes(): Promise<QrCodeItem[]> {
  return apiFetch<QrCodeItem[]>("/admin/qr-codes");
}

export async function createAdminQrCode(data: QrCodeRequest): Promise<QrCodeItem> {
  return apiFetch<QrCodeItem>("/admin/qr-codes", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminQrCode(id: string, data: QrCodeRequest): Promise<QrCodeItem> {
  return apiFetch<QrCodeItem>(`/admin/qr-codes/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminQrCode(id: string): Promise<void> {
  await apiFetch<void>(`/admin/qr-codes/${id}`, { method: "DELETE" });
}

export async function fetchPlatformTenants(): Promise<PlatformTenant[]> {
  return apiFetch<PlatformTenant[]>("/platform/tenants");
}

export async function createPlatformTenant(
  data: PlatformTenantRequest,
): Promise<PlatformTenant> {
  return apiFetch<PlatformTenant>("/platform/tenants", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updatePlatformTenant(
  id: string,
  data: PlatformTenantRequest,
): Promise<PlatformTenant> {
  return apiFetch<PlatformTenant>(`/platform/tenants/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function upsertPlatformTenantAdmin(
  id: string,
  email: string,
  password: string,
): Promise<void> {
  await apiFetch<void>(`/platform/tenants/${id}/admin-user`, {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function deletePlatformTenant(id: string): Promise<void> {
  await apiFetch<void>(`/platform/tenants/${id}`, { method: "DELETE" });
}

export interface FormFieldDto {
  key: string;
  label: string;
  type: "text" | "email" | "phone" | "select" | "checkbox" | "textarea" | "file";
  required: boolean;
  options: string[];
}

export interface FormDto {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  fields: FormFieldDto[];
  notificationRecipients: string[];
  successMessage: string;
  redirectUrl: string | null;
  webhookUrl: string | null;
  hcaptchaEnabled: boolean;
  honeypotField: string;
  systemForm: boolean;
  active: boolean;
}

export interface FormSubmissionDto {
  id: string;
  answers: Record<string, unknown>;
  submitterEmail: string | null;
  readAt: string | null;
  createdAt: string;
}

export type FormRequest = Omit<FormDto, "id" | "systemForm">;

export async function fetchAdminForms(): Promise<FormDto[]> {
  return apiFetch<FormDto[]>("/admin/forms");
}

export async function createAdminForm(data: FormRequest): Promise<FormDto> {
  return apiFetch<FormDto>("/admin/forms", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminForm(id: string, data: FormRequest): Promise<FormDto> {
  return apiFetch<FormDto>(`/admin/forms/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function fetchFormSubmissions(id: string): Promise<FormSubmissionDto[]> {
  return apiFetch<FormSubmissionDto[]>(`/admin/forms/${id}/submissions`);
}

export async function markFormSubmissionRead(id: string): Promise<FormSubmissionDto> {
  return apiFetch<FormSubmissionDto>(`/admin/forms/submissions/${id}/read`, { method: "PATCH" });
}

export function formSubmissionsCsvUrl(id: string): string {
  return buildUrl(`/admin/forms/${id}/submissions.csv`);
}

export interface PublicFormDto {
  name: string;
  slug: string;
  description: string | null;
  fields: FormFieldDto[];
  hcaptchaEnabled: boolean;
  honeypotField: string;
}

export interface SubmitFormResult {
  successMessage: string;
  redirectUrl: string | null;
}

export async function fetchPublicForm(slug: string): Promise<PublicFormDto> {
  return apiFetch<PublicFormDto>(`/forms/${slug}`);
}

export async function submitPublicForm(
  slug: string,
  answers: Record<string, unknown>,
  captchaToken?: string,
): Promise<SubmitFormResult> {
  return apiFetch<SubmitFormResult>(`/forms/${slug}/submit`, {
    method: "POST",
    body: JSON.stringify({ answers, captchaToken }),
  });
}

export interface RedirectRuleDto {
  id: string;
  sourcePath: string;
  targetUrl: string;
  statusCode: 301 | 302;
  hitCount: number;
  active: boolean;
}

export interface NotFoundEventDto {
  id: string;
  path: string;
  hitCount: number;
  lastSeenAt: string;
}

export async function fetchRedirectRules(): Promise<RedirectRuleDto[]> {
  return apiFetch<RedirectRuleDto[]>("/admin/seo/redirects");
}

export async function createRedirectRule(data: {
  sourcePath: string;
  targetUrl: string;
  statusCode: 301 | 302;
  active: boolean;
}): Promise<RedirectRuleDto> {
  return apiFetch<RedirectRuleDto>("/admin/seo/redirects", { method: "POST", body: JSON.stringify(data) });
}

export async function fetchNotFoundEvents(page = 0, size = 50): Promise<NotFoundEventDto[]> {
  return apiFetch<NotFoundEventDto[]>("/admin/seo/not-found", { params: { page, size } });
}

export async function recordNotFound(path: string): Promise<void> {
  await apiFetch<void>("/seo/not-found", { method: "POST", body: JSON.stringify({ path }) });
}

export interface GdprRequestDto {
  id: string;
  requestType: "EXPORT" | "DELETE";
  scope: "USER" | "TENANT";
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  resultJson: Record<string, unknown> | null;
  errorMessage: string | null;
  approvedByUserId: string | null;
  createdAt: string;
  completedAt: string | null;
}

export async function fetchGdprRequests(): Promise<GdprRequestDto[]> {
  return apiFetch<GdprRequestDto[]>("/admin/gdpr/requests");
}

export async function exportMyData(): Promise<GdprRequestDto> {
  return apiFetch<GdprRequestDto>("/admin/gdpr/export", { method: "POST" });
}

export async function deleteMyData(): Promise<GdprRequestDto> {
  return apiFetch<GdprRequestDto>("/admin/gdpr/delete", { method: "POST" });
}

export async function exportTenantData(): Promise<GdprRequestDto> {
  return apiFetch<GdprRequestDto>("/admin/gdpr/export/tenant", { method: "POST" });
}

export async function deleteTenantData(approvedByUserId: string): Promise<GdprRequestDto> {
  return apiFetch<GdprRequestDto>("/admin/gdpr/delete/tenant", {
    method: "POST",
    body: JSON.stringify({ approvedByUserId }),
  });
}

export async function fetchTaxonomyTerms(type: TaxonomyType): Promise<TaxonomyTerm[]> {
  return apiFetch<TaxonomyTerm[]>("/taxonomies", { params: { type } });
}

export async function fetchAdminTaxonomyTerms(type: TaxonomyType): Promise<TaxonomyTerm[]> {
  return apiFetch<TaxonomyTerm[]>("/admin/taxonomies", { params: { type } });
}

export async function createTaxonomyTerm(data: TaxonomyTermRequest): Promise<TaxonomyTerm> {
  return apiFetch<TaxonomyTerm>("/admin/taxonomies", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateTaxonomyTerm(id: string, data: TaxonomyTermRequest): Promise<TaxonomyTerm> {
  return apiFetch<TaxonomyTerm>(`/admin/taxonomies/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteTaxonomyTerm(id: string): Promise<void> {
  await apiFetch<void>(`/admin/taxonomies/${id}`, { method: "DELETE" });
}

export async function fetchProducts(
  filters: ProductFilters = {},
  page = 0,
  size = 24,
): Promise<PagedResponse<ProductSummary>> {
  return apiFetch<PagedResponse<ProductSummary>>("/products", { params: { ...filters, page, size } });
}

export async function fetchProduct(slug: string): Promise<ProductDetail> {
  return apiFetch<ProductDetail>(`/products/${encodeURIComponent(slug)}`);
}

export async function fetchAdminProducts(): Promise<ProductDetail[]> {
  return apiFetch<ProductDetail[]>("/admin/products");
}

export async function createProduct(data: ProductRequest): Promise<ProductDetail> {
  return apiFetch<ProductDetail>("/admin/products", { method: "POST", body: JSON.stringify(data) });
}

export async function updateProduct(id: string, data: ProductRequest): Promise<ProductDetail> {
  return apiFetch<ProductDetail>(`/admin/products/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function publishProduct(id: string, published: boolean): Promise<ProductDetail> {
  return apiFetch<ProductDetail>(`/admin/products/${id}/publish`, { method: "PATCH", params: { published } });
}

export async function deleteProduct(id: string): Promise<void> {
  await apiFetch<void>(`/admin/products/${id}`, { method: "DELETE" });
}

export async function fetchTherapists(filters: TherapistFilters = {}, page = 0, size = 20): Promise<PagedResponse<TherapistSummary>> {
  return apiFetch<PagedResponse<TherapistSummary>>("/therapists", { params: { ...filters, page, size } });
}

export async function fetchTherapist(slug: string): Promise<TherapistDetail> {
  return apiFetch<TherapistDetail>(`/therapists/${encodeURIComponent(slug)}`);
}

export async function fetchAdminTherapists(): Promise<TherapistDetail[]> {
  return apiFetch<TherapistDetail[]>("/admin/therapists");
}

export async function createTherapist(data: TherapistRequest): Promise<TherapistDetail> {
  return apiFetch<TherapistDetail>("/admin/therapists", { method: "POST", body: JSON.stringify(data) });
}

export async function updateTherapist(id: string, data: TherapistRequest): Promise<TherapistDetail> {
  return apiFetch<TherapistDetail>(`/admin/therapists/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function publishTherapist(id: string, published: boolean): Promise<TherapistDetail> {
  return apiFetch<TherapistDetail>(`/admin/therapists/${id}/publish`, { method: "PATCH", params: { published } });
}

export async function deleteTherapist(id: string): Promise<void> {
  await apiFetch<void>(`/admin/therapists/${id}`, { method: "DELETE" });
}

export async function fetchOwnTherapistProfile(): Promise<TherapistDetail> {
  return apiFetch<TherapistDetail>("/me/therapist");
}

export async function updateOwnTherapistProfile(data: Omit<TherapistRequest, "userId" | "staffMemberId" | "published" | "sortOrder">): Promise<TherapistDetail> {
  return apiFetch<TherapistDetail>("/me/therapist", { method: "PUT", body: JSON.stringify(data) });
}

export async function fetchTherapistMatches(data: TherapistMatchRequest): Promise<TherapistMatchResponse> {
  return apiFetch<TherapistMatchResponse>("/therapist-matching/recommendations", { method: "POST", body: JSON.stringify(data) });
}

export interface RssSource {
  id: string; name: string; feedUrl: string; siteUrl: string | null; active: boolean;
  sortOrder: number; syncIntervalMinutes: number; lastSyncedAt: string | null;
  lastSuccessAt: string | null; lastError: string | null; itemCount: number;
}

export interface RssSourceRequest {
  name: string; feedUrl: string; siteUrl?: string; active: boolean;
  sortOrder: number; syncIntervalMinutes: number;
}

export interface RssSyncResult {
  sourceId: string; sourceName: string; received: number; created: number;
  updated: number; notModified: boolean; error: string | null;
}

export async function fetchAdminRssSources(): Promise<RssSource[]> { return apiFetch<RssSource[]>("/admin/rss-sources"); }
export async function createRssSource(data: RssSourceRequest): Promise<RssSource> { return apiFetch<RssSource>("/admin/rss-sources", { method: "POST", body: JSON.stringify(data) }); }
export async function updateRssSource(id: string, data: RssSourceRequest): Promise<RssSource> { return apiFetch<RssSource>(`/admin/rss-sources/${id}`, { method: "PUT", body: JSON.stringify(data) }); }
export async function deleteRssSource(id: string): Promise<void> { await apiFetch<void>(`/admin/rss-sources/${id}`, { method: "DELETE" }); }
export async function syncRssSource(id: string): Promise<RssSyncResult> { return apiFetch<RssSyncResult>(`/admin/rss-sources/${id}/sync`, { method: "POST" }); }
export async function syncAllRssSources(): Promise<RssSyncResult[]> { return apiFetch<RssSyncResult[]>("/admin/rss-sources/sync", { method: "POST" }); }

export interface PodcastSource {
  id: string; name: string; feedUrl: string; siteUrl: string | null; description: string | null;
  imageUrl: string | null; active: boolean; sortOrder: number; syncIntervalMinutes: number;
  lastSyncedAt: string | null; lastSuccessAt: string | null; lastError: string | null; episodeCount: number;
}

export interface PodcastSourceRequest {
  name: string; feedUrl: string; siteUrl?: string; active: boolean;
  sortOrder: number; syncIntervalMinutes: number;
}

export interface PodcastSyncResult {
  sourceId: string; sourceName: string; received: number; created: number;
  updated: number; notModified: boolean; error: string | null;
}

export interface PodcastEpisode {
  id: string; title: string; pageUrl: string | null; audioUrl: string; audioType: string | null;
  audioLength: number | null; description: string | null; imageUrl: string | null;
  duration: string | null; publishedAt: string | null; sourceId: string;
  sourceName: string | null; sourceImageUrl: string | null;
}

export interface PodcastDirectory {
  shows: PodcastSource[];
  episodes: PagedResponse<PodcastEpisode>;
}

export async function fetchAdminPodcastSources(): Promise<PodcastSource[]> { return apiFetch<PodcastSource[]>("/admin/podcast-sources"); }
export async function createPodcastSource(data: PodcastSourceRequest): Promise<PodcastSource> { return apiFetch<PodcastSource>("/admin/podcast-sources", { method: "POST", body: JSON.stringify(data) }); }
export async function updatePodcastSource(id: string, data: PodcastSourceRequest): Promise<PodcastSource> { return apiFetch<PodcastSource>(`/admin/podcast-sources/${id}`, { method: "PUT", body: JSON.stringify(data) }); }
export async function deletePodcastSource(id: string): Promise<void> { await apiFetch<void>(`/admin/podcast-sources/${id}`, { method: "DELETE" }); }
export async function syncPodcastSource(id: string): Promise<PodcastSyncResult> { return apiFetch<PodcastSyncResult>(`/admin/podcast-sources/${id}/sync`, { method: "POST" }); }
export async function syncAllPodcastSources(): Promise<PodcastSyncResult[]> { return apiFetch<PodcastSyncResult[]>("/admin/podcast-sources/sync", { method: "POST" }); }
export async function fetchPodcasts(page = 0, size = 20): Promise<PodcastDirectory> { return apiFetch<PodcastDirectory>("/podcasts", { params: { page, size } }); }

export interface UnifiedBlogItem {
  id: string; kind: "LOCAL" | "RSS"; title: string; excerpt: string | null;
  imageUrl: string | null; publishedAt: string | null; href: string;
  sourceName: string | null; sourceUrl: string | null; readingMinutes: number;
  categories: string[]; viewCount: number; likeCount: number;
}

export interface UnifiedBlogFacets { sources: string[]; categories: string[] }

export async function fetchUnifiedBlog(page = 0, size = 24, filters: { q?: string; source?: string; category?: string } = {}): Promise<PagedResponse<UnifiedBlogItem>> {
  return apiFetch<PagedResponse<UnifiedBlogItem>>("/blog-feed", { params: { page, size, ...filters } });
}
export async function fetchUnifiedBlogFacets(): Promise<UnifiedBlogFacets> { return apiFetch<UnifiedBlogFacets>("/blog-feed/facets"); }

export async function fetchProductMatches(data: ProductMatchRequest): Promise<ProductMatchResponse> {
  return apiFetch<ProductMatchResponse>("/product-matching/recommendations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Restaurant menu (public)
export async function fetchMenuCategories(): Promise<MenuCategory[]> {
  return apiFetch<MenuCategory[]>("/menu/categories");
}

export async function fetchMenuItems(): Promise<MenuItem[]> {
  return apiFetch<MenuItem[]>("/menu/items");
}

// Restaurant menu (admin)
export async function fetchAdminMenuCategories(): Promise<MenuCategory[]> {
  return apiFetch<MenuCategory[]>("/admin/restaurant/menu/categories");
}

export async function createAdminMenuCategory(data: MenuCategoryRequest): Promise<MenuCategory> {
  return apiFetch<MenuCategory>("/admin/restaurant/menu/categories", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdminMenuCategory(id: string, data: MenuCategoryRequest): Promise<MenuCategory> {
  return apiFetch<MenuCategory>(`/admin/restaurant/menu/categories/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminMenuCategory(id: string): Promise<void> {
  await apiFetch<void>(`/admin/restaurant/menu/categories/${id}`, { method: "DELETE" });
}

export async function fetchAdminMenuItems(): Promise<MenuItem[]> {
  return apiFetch<MenuItem[]>("/admin/restaurant/menu/items");
}

export async function createAdminMenuItem(data: MenuItemRequest): Promise<MenuItem> {
  return apiFetch<MenuItem>("/admin/restaurant/menu/items", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAdminMenuItem(id: string, data: MenuItemRequest): Promise<MenuItem> {
  return apiFetch<MenuItem>(`/admin/restaurant/menu/items/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminMenuItem(id: string): Promise<void> {
  await apiFetch<void>(`/admin/restaurant/menu/items/${id}`, { method: "DELETE" });
}

// Radio shows (admin)
export async function fetchAdminRadioShows(): Promise<RadioShow[]> {
  return apiFetch<RadioShow[]>("/admin/radio/shows");
}

export async function createRadioShow(data: RadioShowRequest): Promise<RadioShow> {
  return apiFetch<RadioShow>("/admin/radio/shows", { method: "POST", body: JSON.stringify(data) });
}

export async function updateRadioShow(id: string, data: RadioShowRequest): Promise<RadioShow> {
  return apiFetch<RadioShow>(`/admin/radio/shows/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteRadioShow(id: string): Promise<void> {
  await apiFetch<void>(`/admin/radio/shows/${id}`, { method: "DELETE" });
}

// Radio schedule (admin)
export async function fetchAdminRadioSchedules(): Promise<RadioSchedule[]> {
  return apiFetch<RadioSchedule[]>("/admin/radio/schedules");
}

export async function createRadioSchedule(data: RadioScheduleRequest): Promise<RadioSchedule> {
  return apiFetch<RadioSchedule>("/admin/radio/schedules", { method: "POST", body: JSON.stringify(data) });
}

export async function updateRadioSchedule(id: string, data: RadioScheduleRequest): Promise<RadioSchedule> {
  return apiFetch<RadioSchedule>(`/admin/radio/schedules/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteRadioSchedule(id: string): Promise<void> {
  await apiFetch<void>(`/admin/radio/schedules/${id}`, { method: "DELETE" });
}

// Radio (public)
export async function fetchRadioShows(): Promise<RadioShow[]> {
  return apiFetch<RadioShow[]>("/radio/shows");
}

export async function fetchRadioScheduleByDay(dayOfWeek: number): Promise<RadioSchedule[]> {
  return apiFetch<RadioSchedule[]>(`/radio/schedule/${dayOfWeek}`);
}

export async function fetchRadioLineup(): Promise<RadioLineup> {
  return apiFetch<RadioLineup>("/radio/schedule/lineup");
}

// Gallery (admin)
export async function fetchAdminGalleryAlbums(): Promise<GalleryAlbumDto[]> {
  return apiFetch<GalleryAlbumDto[]>("/admin/art-gallery/albums");
}

export async function createGalleryAlbum(data: GalleryAlbumRequest): Promise<GalleryAlbumDto> {
  return apiFetch<GalleryAlbumDto>("/admin/art-gallery/albums", { method: "POST", body: JSON.stringify(data) });
}

export async function updateGalleryAlbum(id: string, data: GalleryAlbumRequest): Promise<GalleryAlbumDto> {
  return apiFetch<GalleryAlbumDto>(`/admin/art-gallery/albums/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteGalleryAlbum(id: string): Promise<void> {
  await apiFetch<void>(`/admin/art-gallery/albums/${id}`, { method: "DELETE" });
}

export async function fetchAdminGalleryImages(): Promise<GalleryImageDto[]> {
  return apiFetch<GalleryImageDto[]>("/admin/art-gallery/images");
}

export async function fetchAdminGalleryImagesByAlbum(albumId: string): Promise<GalleryImageDto[]> {
  return apiFetch<GalleryImageDto[]>(`/admin/art-gallery/albums/${albumId}/images`);
}

export async function createGalleryImage(data: GalleryImageRequest): Promise<GalleryImageDto> {
  return apiFetch<GalleryImageDto>("/admin/art-gallery/images", { method: "POST", body: JSON.stringify(data) });
}

export async function updateGalleryImage(id: string, data: GalleryImageRequest): Promise<GalleryImageDto> {
  return apiFetch<GalleryImageDto>(`/admin/art-gallery/images/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteGalleryImage(id: string): Promise<void> {
  await apiFetch<void>(`/admin/art-gallery/images/${id}`, { method: "DELETE" });
}

// Gallery (public)
export async function fetchGalleryAlbums(): Promise<GalleryAlbumDto[]> {
  return apiFetch<GalleryAlbumDto[]>("/art-gallery/albums");
}

export async function fetchGalleryAlbum(slug: string): Promise<GalleryAlbumDto> {
  return apiFetch<GalleryAlbumDto>(`/art-gallery/albums/${slug}`);
}

export async function fetchGalleryImagesByAlbum(slug: string): Promise<GalleryImageDto[]> {
  return apiFetch<GalleryImageDto[]>(`/art-gallery/albums/${slug}/images`);
}

export async function fetchGalleryImage(slug: string): Promise<GalleryImageDto> {
  return apiFetch<GalleryImageDto>(`/art-gallery/images/${slug}`);
}

// Cart cookies
const CART_TOKEN_COOKIE = "cart_token";

export function getCartToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${CART_TOKEN_COOKIE}=([^;]*)`));
  return match?.[1];
}

export function setCartToken(token: string, days = 365): void {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${CART_TOKEN_COOKIE}=${token}; expires=${expires}; path=/; SameSite=Lax`;
}

export function clearCartToken(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${CART_TOKEN_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

// Cart API
export async function fetchCart(): Promise<Cart> {
  return apiFetch<Cart>("/cart");
}

export async function addCartItem(request: CartItemRequest): Promise<Cart> {
  return apiFetch<Cart>("/cart/items", { method: "POST", body: JSON.stringify(request) });
}

export async function updateCartItem(id: string, quantity: number): Promise<Cart> {
  return apiFetch<Cart>(`/cart/items/${id}`, {
    method: "PUT",
    body: JSON.stringify({ productId: "", quantity }),
  });
}

export async function removeCartItem(id: string): Promise<Cart> {
  return apiFetch<Cart>(`/cart/items/${id}`, { method: "DELETE" });
}

export async function clearCart(): Promise<Cart> {
  return apiFetch<Cart>("/cart", { method: "DELETE" });
}

export async function createCheckout(data: {
  customerEmail?: string;
  customerName?: string;
  couponCode?: string;
  fulfillmentMethod?: "PICKUP" | "SHIP";
  shippingAddress?: { line1: string; line2?: string; city: string; state: string; postalCode: string };
  pickupLocation?: string;
} = {}): Promise<CheckoutSession> {
  return apiFetch<CheckoutSession>("/checkout", { method: "POST", body: JSON.stringify(data) });
}

// Admin Coupons
export async function fetchCoupons(): Promise<import("@/types/cart").Coupon[]> {
  return apiFetch<import("@/types/cart").Coupon[]>("/admin/coupons");
}

export async function fetchCoupon(id: string): Promise<import("@/types/cart").Coupon> {
  return apiFetch<import("@/types/cart").Coupon>(`/admin/coupons/${id}`);
}

export async function createCoupon(data: Partial<import("@/types/cart").Coupon>): Promise<import("@/types/cart").Coupon> {
  return apiFetch<import("@/types/cart").Coupon>("/admin/coupons", { method: "POST", body: JSON.stringify(data) });
}

export async function updateCoupon(id: string, data: Partial<import("@/types/cart").Coupon>): Promise<import("@/types/cart").Coupon> {
  return apiFetch<import("@/types/cart").Coupon>(`/admin/coupons/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deactivateCoupon(id: string): Promise<void> {
  return apiFetch<void>(`/admin/coupons/${id}`, { method: "DELETE" });
}

// Admin Orders
export async function fetchAdminOrders(status?: string, page = 0): Promise<import("@/types/cart").Order[]> {
  const query = `?page=${page}&size=50${status ? `&status=${encodeURIComponent(status)}` : ""}`;
  return apiFetch<import("@/types/cart").Order[]>(`/admin/orders${query}`);
}

export async function fetchAdminOrder(id: string): Promise<import("@/types/cart").Order> {
  return apiFetch<import("@/types/cart").Order>(`/admin/orders/${id}`);
}

export async function updateOrderStatus(id: string, status: string): Promise<import("@/types/cart").Order> {
  return apiFetch<import("@/types/cart").Order>(`/admin/orders/${id}/status`, { method: "PUT", body: JSON.stringify({ status }) });
}

export async function fetchOrder(id: string): Promise<Order> {
  return apiFetch<Order>(`/checkout/orders/${id}`);
}

export async function cancelCheckout(id: string): Promise<Order> {
  return apiFetch<Order>(`/checkout/orders/${id}/cancel`, { method: "POST" });
}

// Booking (public)
export async function fetchBookableServices(): Promise<BookableService[]> {
  return apiFetch<BookableService[]>("/booking/services");
}

export async function fetchServiceSlots(slug: string, date: string): Promise<Slot[]> {
  return apiFetch<Slot[]>(`/booking/services/${encodeURIComponent(slug)}/slots`, { params: { date } });
}

export async function createBooking(request: PublicBookingRequest): Promise<Appointment> {
  return apiFetch<Appointment>("/booking/appointments", { method: "POST", body: JSON.stringify(request) });
}

export async function fetchAppointment(id: string, email: string): Promise<Appointment> {
  return apiFetch<Appointment>(`/booking/appointments/${id}`, { params: { email } });
}

export function fetchBookingCalendarIcsUrl(): string {
  return buildUrl("/booking/calendar.ics");
}

// Booking (admin)
export async function fetchAdminBookableServices(): Promise<BookableService[]> {
  return apiFetch<BookableService[]>("/admin/booking/services");
}

export async function createAdminBookableService(data: BookableServiceRequest): Promise<BookableService> {
  return apiFetch<BookableService>("/admin/booking/services", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminBookableService(id: string, data: BookableServiceRequest): Promise<BookableService> {
  return apiFetch<BookableService>(`/admin/booking/services/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminBookableService(id: string): Promise<void> {
  await apiFetch<void>(`/admin/booking/services/${id}`, { method: "DELETE" });
}

export async function fetchAdminBookingAvailability(): Promise<BookingAvailability[]> {
  return apiFetch<BookingAvailability[]>("/admin/booking/availability");
}

export async function createAdminBookingAvailability(data: BookingAvailabilityRequest): Promise<BookingAvailability> {
  return apiFetch<BookingAvailability>("/admin/booking/availability", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminBookingAvailability(
  id: string,
  data: BookingAvailabilityRequest,
): Promise<BookingAvailability> {
  return apiFetch<BookingAvailability>(`/admin/booking/availability/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminBookingAvailability(id: string): Promise<void> {
  await apiFetch<void>(`/admin/booking/availability/${id}`, { method: "DELETE" });
}

export async function markAppointmentsViewed(ids: string[]): Promise<number> {
  return apiFetch<number>("/admin/booking/appointments/mark-viewed", {
    method: "POST",
    body: JSON.stringify(ids),
  });
}

export async function fetchAdminAppointments(status?: Appointment["status"]): Promise<Appointment[]> {
  return apiFetch<Appointment[]>("/admin/booking/appointments", { params: status ? { status } : {} });
}

export async function updateAppointmentStatus(id: string, status: Appointment["status"]): Promise<Appointment> {
  return apiFetch<Appointment>(`/admin/booking/appointments/${id}/status`, {
    method: "PATCH",
    params: { status },
  });
}

export async function deleteAdminAppointment(id: string): Promise<void> {
  await apiFetch<void>(`/admin/booking/appointments/${id}`, { method: "DELETE" });
}

export async function fetchCalendarConnections(): Promise<CalendarConnection[]> {
  return apiFetch<CalendarConnection[]>("/admin/calendar-connections");
}

export async function disconnectCalendarConnection(id: string): Promise<void> {
  await apiFetch<void>(`/admin/calendar-connections/${id}`, { method: "DELETE" });
}

export async function updateCalendarConnection(
  id: string,
  data: { enabled?: boolean; calendarId?: string | null },
): Promise<CalendarConnection> {
  return apiFetch<CalendarConnection>(`/admin/calendar-connections/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function getGoogleCalendarAuthUrl(): Promise<string> {
  return apiFetch<string>("/admin/calendar/oauth/google");
}

export async function fetchClub(): Promise<ClubPublic> {
  return apiFetch<ClubPublic>("/club");
}

export async function fetchClubAccount(): Promise<import("@/types/club").ClubAccount> {
  return apiFetch("/club/account");
}

export async function updateClubAccount(data: { displayName: string; phone: string; privacy: import("@/types/club").ClubPrivacy }): Promise<import("@/types/club").ClubAccount> {
  return apiFetch("/club/account", { method: "PUT", body: JSON.stringify(data) });
}

export async function fetchClubCapabilities(): Promise<string[]> { return apiFetch("/club/account/capabilities"); }
export async function fetchClubDuesMembers(): Promise<import("@/types/club").ClubMemberPublic[]> { return apiFetch("/club/account/dues-members"); }
export async function uploadClubDocument(id: string, file: File): Promise<void> {
  const body = new FormData();
  body.append("file", file);
  return apiFetch(`/club/account/documents/${id}/file`, { method: "POST", body });
}

export function fetchClubCalendarIcsUrl(): string {
  return buildUrl("/club/calendar.ics");
}

export async function createClubRsvp(data: {
  eventId: string;
  guestName: string;
  guestEmail: string;
  status?: ClubRsvpStatus;
}): Promise<ClubRsvp> {
  return apiFetch<ClubRsvp>("/club/rsvps", { method: "POST", body: JSON.stringify(data) });
}

export async function fetchClubRsvpCounts(eventId: string): Promise<ClubRsvpCounts> {
  return apiFetch<ClubRsvpCounts>(`/club/events/${eventId}/rsvps`);
}

export async function checkoutClubDues(email: string): Promise<ClubDuesCheckout> {
  return apiFetch<ClubDuesCheckout>("/club/dues/checkout", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function fetchAdminClubProfile(): Promise<ClubPublic> {
  return apiFetch<ClubPublic>("/admin/club/profile");
}

export async function updateAdminClubProfile(data: { name?: string; about?: string }): Promise<ClubPublic> {
  return apiFetch<ClubPublic>("/admin/club/profile", { method: "PUT", body: JSON.stringify(data) });
}

export async function fetchAdminClubMembers(): Promise<ClubMember[]> {
  return apiFetch<ClubMember[]>("/admin/club/members");
}

export async function createAdminClubMember(data: ClubMemberRequest): Promise<ClubMember> {
  return apiFetch<ClubMember>("/admin/club/members", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminClubMember(id: string, data: ClubMemberRequest): Promise<ClubMember> {
  return apiFetch<ClubMember>(`/admin/club/members/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminClubMember(id: string): Promise<void> {
  await apiFetch<void>(`/admin/club/members/${id}`, { method: "DELETE" });
}

export async function fetchAdminClubDocuments(): Promise<ClubDocument[]> {
  return apiFetch<ClubDocument[]>("/admin/club/documents");
}

export async function createAdminClubDocument(data: ClubDocumentRequest): Promise<ClubDocument> {
  return apiFetch<ClubDocument>("/admin/club/documents", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminClubDocument(id: string, data: ClubDocumentRequest): Promise<ClubDocument> {
  return apiFetch<ClubDocument>(`/admin/club/documents/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminClubDocument(id: string): Promise<void> {
  await apiFetch<void>(`/admin/club/documents/${id}`, { method: "DELETE" });
}

export async function fetchAdminClubTiers(): Promise<ClubTier[]> {
  return apiFetch<ClubTier[]>("/admin/club/tiers");
}

export async function createAdminClubTier(data: ClubTierRequest): Promise<ClubTier> {
  return apiFetch<ClubTier>("/admin/club/tiers", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminClubTier(id: string, data: ClubTierRequest): Promise<ClubTier> {
  return apiFetch<ClubTier>(`/admin/club/tiers/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminClubTier(id: string): Promise<void> {
  await apiFetch<void>(`/admin/club/tiers/${id}`, { method: "DELETE" });
}

export async function fetchAdminClubDues(): Promise<ClubDues[]> {
  return apiFetch<ClubDues[]>("/admin/club/dues");
}

export async function createAdminClubDues(data: ClubDuesRequest): Promise<ClubDues> {
  return apiFetch<ClubDues>("/admin/club/dues", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminClubDues(id: string, data: ClubDuesRequest): Promise<ClubDues> {
  return apiFetch<ClubDues>(`/admin/club/dues/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminClubDues(id: string): Promise<void> {
  await apiFetch<void>(`/admin/club/dues/${id}`, { method: "DELETE" });
}

export async function generateClubRenewals(): Promise<{ created: number }> {
  return apiFetch<{ created: number }>("/admin/club/dues/renewals", { method: "POST" });
}

export async function fetchAdminClubMeetings(): Promise<ClubMeeting[]> {
  return apiFetch<ClubMeeting[]>("/admin/club/meetings");
}

export async function createAdminClubMeeting(data: ClubMeetingRequest): Promise<ClubMeeting> {
  return apiFetch<ClubMeeting>("/admin/club/meetings", { method: "POST", body: JSON.stringify(data) });
}

export async function updateAdminClubMeeting(id: string, data: ClubMeetingRequest): Promise<ClubMeeting> {
  return apiFetch<ClubMeeting>(`/admin/club/meetings/${id}`, { method: "PUT", body: JSON.stringify(data) });
}

export async function deleteAdminClubMeeting(id: string): Promise<void> {
  await apiFetch<void>(`/admin/club/meetings/${id}`, { method: "DELETE" });
}

export async function fetchAdminClubRsvps(): Promise<ClubRsvp[]> {
  return apiFetch<ClubRsvp[]>("/admin/club/rsvps");
}

export async function deleteAdminClubRsvp(id: string): Promise<void> {
  await apiFetch<void>(`/admin/club/rsvps/${id}`, { method: "DELETE" });
}
